import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Bell, BarChart3, FileText, Users, Megaphone, Camera, Tag, AlertTriangle,
  TrendingUp, ArrowUpRight, Trash2, Plus, Send, Clock, UserX, Star,
  Copy, Repeat, MessageCircle, ArrowUp, ArrowDown, RefreshCw, Database
} from 'lucide-react';

const AdminAdvancedTools = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const isAdmin = ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email) || user?.is_admin;

  // Data states
  const [alerts, setAlerts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [serialCancellers, setSerialCancellers] = useState([]);
  const [inactivePlayers, setInactivePlayers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [users, setUsers] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loyalty, setLoyalty] = useState([]);
  const [waLinks, setWaLinks] = useState([]);
  // Advanced WhatsApp Broadcast state
  const [msgTemplates, setMsgTemplates] = useState([]);
  const [waRecipientType, setWaRecipientType] = useState('tournament_not_registered');
  const [waSkillLevel, setWaSkillLevel] = useState('דרג א');
  const [waMessage, setWaMessage] = useState('היי {name}! 🎾\nתזכורת ל-{tournament} ב-{date}.\nלפרטים: https://tennisproisrael.co.il');
  const [newTplForm, setNewTplForm] = useState({ name: '', content: '', category: 'general' });
  const [loading, setLoading] = useState(true);

  // Form states
  const [newTemplate, setNewTemplate] = useState({ name: '', tournament_name: '', max_players: 16, price: 0, location: 'מרכז הטניס רעננה', time: '09:00', level: '', type: 'daily', category: 'דרג ג' });
  const [templateDate, setTemplateDate] = useState({});
  const [recurring, setRecurring] = useState({}); // {templateId: {start_date, count, interval}}
  const [bulkPlayers, setBulkPlayers] = useState('');
  const [bulkTournament, setBulkTournament] = useState('');
  const [forcePlayer, setForcePlayer] = useState('');
  const [forceTournament, setForceTournament] = useState('');
  const [reminderTournament, setReminderTournament] = useState('');
  const [waTournament, setWaTournament] = useState('');
  const [newCode, setNewCode] = useState({ code: '', discount_percent: 10, max_uses: 100 });
  const [duplicateTournament, setDuplicateTournament] = useState({ id: '', date: '' });

  // Gallery
  const [photoTournament, setPhotoTournament] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoCaption, setPhotoCaption] = useState('');
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState('alerts');
  const [recomputing, setRecomputing] = useState(false);

  const headers = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    loadAll();
    // eslint-disable-next-line
  }, [isAdmin]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, an, ph, sc, ip, t, tour, u, c, ly, mt] = await Promise.all([
        axios.get(`${API}/admin/alerts`, { headers: headers() }),
        axios.get(`${API}/admin/analytics`, { headers: headers() }),
        axios.get(`${API}/admin/analytics/peak-hours`, { headers: headers() }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/analytics/serial-cancellers`, { headers: headers() }).catch(() => ({ data: { cancellers: [] } })),
        axios.get(`${API}/admin/analytics/inactive-players`, { headers: headers() }).catch(() => ({ data: { inactive: [] } })),
        axios.get(`${API}/admin/tournament-templates`, { headers: headers() }),
        axios.get(`${API}/tournaments`),
        axios.get(`${API}/admin/users`, { headers: headers() }),
        axios.get(`${API}/admin/discount-codes`, { headers: headers() }),
        axios.get(`${API}/admin/loyalty-points`, { headers: headers() }).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/admin/message-templates`, { headers: headers() }).catch(() => ({ data: { templates: [] } })),
      ]);
      setAlerts(a.data.alerts || []);
      setAnalytics(an.data);
      setPeakHours(ph.data);
      setSerialCancellers(sc.data.cancellers || []);
      setInactivePlayers(ip.data.inactive || []);
      setTemplates(t.data.templates || []);
      setTournaments(Array.isArray(tour.data) ? tour.data : (tour.data?.tournaments || []));
      setUsers(Array.isArray(u.data) ? u.data : (u.data?.users || []));
      setDiscountCodes(c.data.codes || []);
      setLoyalty(ly.data.users || []);
      setMsgTemplates(mt.data.templates || []);
    } catch (e) {
      toast.error('שגיאה בטעינת נתונים');
    } finally {
      setLoading(false);
    }
  };

  // === TEMPLATES ===
  const createTemplate = async () => {
    if (!newTemplate.name || !newTemplate.tournament_name) { toast.error('חובה לציין שם תבנית ושם תחרות'); return; }
    try {
      await axios.post(`${API}/admin/tournament-templates`, newTemplate, { headers: headers() });
      toast.success('תבנית נשמרה');
      setNewTemplate({ name: '', tournament_name: '', max_players: 16, price: 0, location: 'מרכז הטניס רעננה', time: '09:00', level: '', type: 'daily', category: 'דרג ג' });
      loadAll();
    } catch (e) { toast.error('שגיאה בשמירת תבנית'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('למחוק תבנית זו?')) return;
    await axios.delete(`${API}/admin/tournament-templates/${id}`, { headers: headers() });
    toast.success('נמחק'); loadAll();
  };

  const createFromTemplate = async (templateId) => {
    const date = templateDate[templateId];
    if (!date) { toast.error('בחר תאריך לתחרות החדשה'); return; }
    try {
      await axios.post(`${API}/admin/tournaments/from-template/${templateId}`, { date }, { headers: headers() });
      toast.success('תחרות נוצרה מתבנית');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  const createRecurring = async (templateId) => {
    const r = recurring[templateId] || {};
    if (!r.start_date) { toast.error('בחר תאריך התחלה'); return; }
    try {
      const res = await axios.post(`${API}/admin/tournament-templates/${templateId}/recurring`, {
        start_date: r.start_date,
        count: r.count || 4,
        interval_days: r.interval_days || 7,
      }, { headers: headers() });
      toast.success(res.data.message);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  // === BULK / FORCE ===
  const bulkRegister = async () => {
    if (!bulkTournament || !bulkPlayers.trim()) { toast.error('בחר תחרות והזן שמות שחקנים'); return; }
    const names = bulkPlayers.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    const playerIds = users.filter(u => names.some(n => (u.full_name || '').includes(n) || u.email === n)).map(u => u.id);
    if (playerIds.length === 0) { toast.error('לא נמצאו שחקנים מתאימים'); return; }
    try {
      const res = await axios.post(`${API}/admin/tournaments/${bulkTournament}/bulk-register`, { player_ids: playerIds }, { headers: headers() });
      toast.success(`נוספו: ${res.data.added_to_main.length} רשומים, ${res.data.added_to_waiting.length} ברשימת המתנה, ${res.data.skipped_already_registered.length} דולגו`);
      setBulkPlayers('');
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  const forceRegister = async () => {
    if (!forceTournament || !forcePlayer) { toast.error('בחר תחרות ושחקן'); return; }
    try {
      const res = await axios.post(`${API}/admin/tournaments/${forceTournament}/force-register/${forcePlayer}`, {}, { headers: headers() });
      toast.success(res.data.message);
      setForcePlayer('');
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  // === MARKETING ===
  const sendReminder = async () => {
    if (!reminderTournament) { toast.error('בחר תחרות'); return; }
    if (!window.confirm('לשלוח תזכורת לכל המשתמשים שאינם רשומים?')) return;
    try {
      const res = await axios.post(`${API}/admin/marketing/reminder-broadcast/${reminderTournament}`, {}, { headers: headers() });
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  const generateWaLinks = async () => {
    if (!waTournament) { toast.error('בחר תחרות'); return; }
    try {
      const res = await axios.post(`${API}/admin/marketing/whatsapp-broadcast/${waTournament}`, {}, { headers: headers() });
      setWaLinks(res.data.targets || []);
      toast.success(`נוצרו ${res.data.count} קישורי WhatsApp`);
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  // === Advanced WhatsApp Broadcast ===
  const generateWaAdvanced = async () => {
    if (!waMessage.trim()) { toast.error('הזן הודעה'); return; }
    const recipients = { type: waRecipientType };
    if (waRecipientType.startsWith('tournament_')) {
      if (!waTournament) { toast.error('בחר תחרות'); return; }
      recipients.tournament_id = waTournament;
    } else if (waRecipientType === 'by_skill_level') {
      recipients.skill_level = waSkillLevel;
    }
    try {
      const res = await axios.post(`${API}/admin/marketing/whatsapp-broadcast-advanced`,
        { message: waMessage, recipients }, { headers: headers() });
      setWaLinks(res.data.targets || []);
      const skip = res.data.skipped_no_phone || 0;
      toast.success(`${res.data.count} קישורים נוצרו${skip ? ` (${skip} ללא טלפון)` : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  const useTemplate = (tpl) => {
    setWaMessage(tpl.content);
    toast.success(`תבנית "${tpl.name}" נטענה`);
  };

  const saveMessageTemplate = async () => {
    if (!newTplForm.name.trim() || !newTplForm.content.trim()) { toast.error('שם ותוכן חובה'); return; }
    try {
      await axios.post(`${API}/admin/message-templates`, newTplForm, { headers: headers() });
      toast.success('תבנית נשמרה');
      setNewTplForm({ name: '', content: '', category: 'general' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  const saveCurrentAsTemplate = async () => {
    const name = window.prompt('שם התבנית:');
    if (!name?.trim()) return;
    try {
      await axios.post(`${API}/admin/message-templates`,
        { name: name.trim(), content: waMessage, category: 'general' }, { headers: headers() });
      toast.success('נשמר כתבנית');
      loadAll();
    } catch (e) { toast.error('שגיאה'); }
  };

  const deleteMsgTemplate = async (id) => {
    if (!window.confirm('למחוק תבנית זו?')) return;
    try {
      await axios.delete(`${API}/admin/message-templates/${id}`, { headers: headers() });
      toast.success('נמחקה'); loadAll();
    } catch (e) { toast.error('שגיאה'); }
  };

  const copyAllLinks = () => {
    if (waLinks.length === 0) return;
    const text = waLinks.map(w => `${w.name}: ${w.whatsapp_url}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success(`הועתקו ${waLinks.length} קישורים`);
  };

  const createDiscountCode = async () => {
    if (!newCode.code) { toast.error('חובה לציין קוד'); return; }
    try {
      await axios.post(`${API}/admin/discount-codes`, newCode, { headers: headers() });
      toast.success('קוד הנחה נוצר');
      setNewCode({ code: '', discount_percent: 10, max_uses: 100 });
      loadAll();
    } catch (e) { toast.error('שגיאה'); }
  };

  const deleteCode = async (id) => {
    if (!window.confirm('למחוק קוד זה?')) return;
    await axios.delete(`${API}/admin/discount-codes/${id}`, { headers: headers() });
    toast.success('נמחק'); loadAll();
  };

  const redeemLoyalty = async (uid) => {
    if (!window.confirm('לממש נקודת נאמנות (תחרות חינם)?')) return;
    try {
      const res = await axios.post(`${API}/admin/loyalty-points/${uid}/redeem`, {}, { headers: headers() });
      toast.success(res.data.message);
      loadAll();
    } catch (e) { toast.error('שגיאה'); }
  };

  // === DUPLICATE TOURNAMENT ===
  const doDuplicate = async () => {
    if (!duplicateTournament.id) { toast.error('בחר תחרות לשכפול'); return; }
    try {
      const res = await axios.post(`${API}/admin/tournaments/${duplicateTournament.id}/duplicate`, { date: duplicateTournament.date }, { headers: headers() });
      toast.success(res.data.message);
      setDuplicateTournament({ id: '', date: '' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  // === GALLERY ===
  const loadPhotos = async (tid) => {
    if (!tid) { setGalleryPhotos([]); return; }
    try {
      const res = await axios.get(`${API}/tournaments/${tid}/photos`);
      setGalleryPhotos(res.data.photos || []);
    } catch { setGalleryPhotos([]); }
  };

  useEffect(() => { loadPhotos(photoTournament); }, [photoTournament]);

  const addPhoto = async () => {
    if (!photoTournament || !photoUrl) { toast.error('בחר תחרות והזן URL תמונה'); return; }
    try {
      await axios.post(`${API}/admin/tournaments/${photoTournament}/photos`, { url: photoUrl, caption: photoCaption }, { headers: headers() });
      toast.success('תמונה נוספה');
      setPhotoUrl(''); setPhotoCaption('');
      loadPhotos(photoTournament);
    } catch (e) { toast.error('שגיאה'); }
  };

  const deletePhoto = async (pid) => {
    if (!window.confirm('למחוק תמונה?')) return;
    await axios.delete(`${API}/admin/tournaments/${photoTournament}/photos/${pid}`, { headers: headers() });
    toast.success('נמחק'); loadPhotos(photoTournament);
  };

  const movePhoto = async (idx, dir) => {
    const newOrder = [...galleryPhotos];
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    setGalleryPhotos(newOrder);
    try {
      await axios.put(`${API}/admin/tournaments/${photoTournament}/photos/reorder`,
        { order: newOrder.map(p => p.id) }, { headers: headers() });
    } catch (e) { toast.error('שגיאה בסידור'); loadPhotos(photoTournament); }
  };

  const recomputeAllStats = async () => {
    if (!window.confirm('לחשב מחדש את כל הסטטיסטיקות (ניצחונות / הפסדים / תחרויות / אחוז ניצחונות) לכל המשתמשים על בסיס התוצאות בפועל?\n\n⚠️ הניקוד לא יושפע (מתעדכן ידנית).')) return;
    setRecomputing(true);
    try {
      const res = await axios.post(`${API}/admin/recompute-all-stats`, {}, { headers: headers() });
      const d = res.data.details || {};
      toast.success(`${res.data.message} (${d.matches_analyzed || 0} משחקים, ${d.completed_tournaments || 0} תחרויות שהסתיימו)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בחישוב מחדש');
    } finally {
      setRecomputing(false);
    }
  };

  if (!isAdmin) return null;
  if (loading) return <div className="min-h-screen flex items-center justify-center" dir="rtl">טוען...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" dir="rtl">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-3xl font-black text-slate-900 flex items-center gap-3">
            🛠️ כלי ניהול מתקדמים
          </h1>
          <p className="text-sm text-slate-500 mt-1">אנליטיקה, תבניות, שיווק, נאמנות וגלריה — הכל במקום אחד</p>
          <Link to="/admin/control" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-2" data-testid="back-to-admin-link">
            ← חזרה לדשבורד הראשי
          </Link>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-1 h-auto bg-white p-2 rounded-2xl shadow-sm mb-6">
            <TabsTrigger value="alerts" data-testid="tab-alerts" className="rounded-xl py-2 text-xs"><Bell className="w-4 h-4 ml-1" />התראות</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-xl py-2 text-xs"><BarChart3 className="w-4 h-4 ml-1" />אנליטיקה</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates" className="rounded-xl py-2 text-xs"><FileText className="w-4 h-4 ml-1" />תבניות</TabsTrigger>
            <TabsTrigger value="duplicate" data-testid="tab-duplicate" className="rounded-xl py-2 text-xs"><Copy className="w-4 h-4 ml-1" />שכפול</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk" className="rounded-xl py-2 text-xs"><Users className="w-4 h-4 ml-1" />הרשמה</TabsTrigger>
            <TabsTrigger value="marketing" data-testid="tab-marketing" className="rounded-xl py-2 text-xs"><Megaphone className="w-4 h-4 ml-1" />שיווק</TabsTrigger>
            <TabsTrigger value="loyalty" data-testid="tab-loyalty" className="rounded-xl py-2 text-xs"><Star className="w-4 h-4 ml-1" />נאמנות</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="rounded-xl py-2 text-xs"><Camera className="w-4 h-4 ml-1" />גלריה</TabsTrigger>
            <TabsTrigger value="system" data-testid="tab-system" className="rounded-xl py-2 text-xs"><Database className="w-4 h-4 ml-1" />מערכת</TabsTrigger>
          </TabsList>

          {/* ALERTS */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-orange-500" />התראות חכמות ({alerts.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {alerts.length === 0 && <p className="text-slate-500 text-center py-6">🎉 אין התראות כרגע - הכל בסדר!</p>}
                {alerts.map((a, i) => (
                  <div key={i} data-testid={`alert-${i}`} className={`p-4 rounded-xl border-2 ${a.severity === 'high' ? 'bg-red-50 border-red-200' : a.severity === 'medium' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-bold text-slate-900">{a.icon} {a.title}</p>
                        <p className="text-sm text-slate-600 mt-0.5">{a.description}</p>
                      </div>
                      {a.tournament_id && <Link to={`/tournament/${a.tournament_id}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 shrink-0"><ArrowUpRight className="w-3 h-3" />פתח</Link>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ANALYTICS */}
          <TabsContent value="analytics" className="space-y-6">
            {analytics && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'משתמשים סה״כ', value: analytics.total_users, color: 'from-blue-500 to-cyan-500', icon: '👥' },
                  { label: 'תחרויות פעילות', value: analytics.active_tournaments, color: 'from-emerald-500 to-teal-500', icon: '🏆' },
                  { label: 'תחרויות שהסתיימו', value: analytics.completed_tournaments, color: 'from-purple-500 to-indigo-500', icon: '✅' },
                  { label: 'ביטולים החודש', value: analytics.cancellations_this_month, color: 'from-red-500 to-rose-500', icon: '❌' },
                  { label: 'לא פעילים (30 ימים)', value: analytics.inactive_users_30d, color: 'from-amber-500 to-orange-500', icon: '😴' },
                  { label: 'ממוצע תפוסה', value: `${analytics.avg_fill_rate}%`, color: 'from-pink-500 to-rose-500', icon: '📊' },
                ].map((s, i) => (
                  <Card key={i} className="overflow-hidden">
                    <CardContent className={`p-3 bg-gradient-to-br ${s.color} text-white`}>
                      <div className="text-xl">{s.icon}</div>
                      <p className="text-2xl font-black mt-1">{s.value}</p>
                      <p className="text-xs opacity-90 mt-1">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Users by month */}
            {analytics && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5" />משתמשים חדשים לפי חודש</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics.users_by_month.map(m => (
                      <div key={m.month} className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 w-20 font-mono">{m.month}</span>
                        <div className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center px-2 text-white text-xs font-bold" style={{ width: `${Math.min(100, m.count * 5)}%` }}>{m.count}</div>
                        </div>
                      </div>
                    ))}
                    {analytics.users_by_month.length === 0 && <p className="text-slate-400 text-sm">אין נתונים</p>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Peak hours / days */}
            {peakHours && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Clock className="w-5 h-5" />שעות שיא של פעילות</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {peakHours.hours.map(h => {
                        const max = Math.max(...peakHours.hours.map(x => x.count), 1);
                        return (
                          <div key={h.hour} className="flex items-center gap-2">
                            <span className="text-xs text-slate-500 w-10 font-mono">{String(h.hour).padStart(2, '0')}:00</span>
                            <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500" style={{ width: `${(h.count / max) * 100}%` }} />
                            </div>
                            <span className="text-xs text-slate-600 w-8 text-left">{h.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2 text-base">📅 ימי שיא של פעילות</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {peakHours.days.map((d, i) => {
                        const max = Math.max(...peakHours.days.map(x => x.count), 1);
                        return (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-xs text-slate-700 w-14 font-medium">{d.day}</span>
                            <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center px-2 text-white text-xs font-bold" style={{ width: `${(d.count / max) * 100}%` }}>{d.count}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Serial cancellers */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><UserX className="w-5 h-5 text-red-500" />מבטלנים סדרתיים (60 ימים)</CardTitle></CardHeader>
              <CardContent>
                {serialCancellers.length === 0 ? <p className="text-slate-400 text-sm">אין נתונים</p> : (
                  <div className="space-y-2">
                    {serialCancellers.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">{c.name}</p>
                          <p className="text-xs text-slate-500">{c.cancellation_count} ביטולים · {c.total_registrations} הרשמות סה״כ · אחוז ביטול {c.cancellation_rate}%</p>
                        </div>
                        <Badge variant="destructive">{c.cancellation_count}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inactive players */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base">😴 שחקנים שהפסיקו להגיע ({inactivePlayers.length})</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">משתמשים שלא נרשמו לאף תחרות ב-30 הימים האחרונים</p>
                {inactivePlayers.length === 0 ? <p className="text-slate-400 text-sm">אין שחקנים לא פעילים 🎉</p> : (
                  <div className="space-y-1 max-h-96 overflow-y-auto">
                    {inactivePlayers.slice(0, 50).map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-amber-50 border border-amber-100 rounded">
                        <div className="flex-1">
                          <span className="font-medium text-sm">{p.full_name}</span>
                          <span className="text-xs text-slate-500 mr-2">{p.skill_level} · {p.phone || 'אין טלפון'}</span>
                        </div>
                        <span className="text-xs text-slate-400">{p.last_tournament_date ? `אחרון: ${p.last_tournament_date.slice(0,10)}` : 'מעולם לא נרשם'}</span>
                      </div>
                    ))}
                    {inactivePlayers.length > 50 && <p className="text-xs text-slate-500 text-center pt-2">+{inactivePlayers.length - 50} נוספים</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TEMPLATES */}
          <TabsContent value="templates">
            <Card className="mb-4">
              <CardHeader><CardTitle>צור תבנית חדשה</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input data-testid="template-name" placeholder="שם תבנית (פנימי)" value={newTemplate.name} onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })} />
                <Input data-testid="template-tournament-name" placeholder="שם תחרות (ציבורי)" value={newTemplate.tournament_name} onChange={e => setNewTemplate({ ...newTemplate, tournament_name: e.target.value })} />
                <select className="border rounded-md p-2" value={newTemplate.type} onChange={e => setNewTemplate({ ...newTemplate, type: e.target.value })}>
                  <option value="daily">יומית</option>
                  <option value="knockout">נוקאאוט</option>
                </select>
                <select className="border rounded-md p-2" value={newTemplate.category} onChange={e => setNewTemplate({ ...newTemplate, category: e.target.value })}>
                  <option value="דרג א">דרג א</option>
                  <option value="דרג ב">דרג ב</option>
                  <option value="דרג ג">דרג ג</option>
                  <option value="יחידים open">יחידים open</option>
                  <option value="זוגות open">זוגות open</option>
                  <option value="נשים">נשים</option>
                </select>
                <Input type="number" placeholder="מקסימום שחקנים" value={newTemplate.max_players} onChange={e => setNewTemplate({ ...newTemplate, max_players: parseInt(e.target.value) || 16 })} />
                <Input type="number" placeholder="מחיר" value={newTemplate.price} onChange={e => setNewTemplate({ ...newTemplate, price: parseInt(e.target.value) || 0 })} />
                <Input placeholder="שעת התחלה (09:00)" value={newTemplate.time} onChange={e => setNewTemplate({ ...newTemplate, time: e.target.value })} />
                <Input className="md:col-span-2" placeholder="מיקום" value={newTemplate.location} onChange={e => setNewTemplate({ ...newTemplate, location: e.target.value })} />
                <Button onClick={createTemplate} data-testid="save-template-btn" className="md:col-span-3 bg-green-600 hover:bg-green-700"><Plus className="w-4 h-4 ml-1" />שמור תבנית</Button>
              </CardContent>
            </Card>
            <div className="space-y-3">
              {templates.map(t => (
                <Card key={t.id}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div>
                        <p className="font-bold text-slate-900">{t.name}</p>
                        <p className="text-sm text-slate-500">{t.tournament_name} · {t.category || t.level || 'ללא רמה'} · {t.max_players} שחקנים · ₪{t.price}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input type="datetime-local" className="w-44" value={templateDate[t.id] || ''} onChange={e => setTemplateDate({ ...templateDate, [t.id]: e.target.value })} />
                        <Button size="sm" onClick={() => createFromTemplate(t.id)} className="bg-blue-600 hover:bg-blue-700">צור 1</Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteTemplate(t.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    {/* Recurring */}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                      <Repeat className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-medium text-slate-600">יצירה אוטומטית של סדרה:</span>
                      <Input type="date" className="w-36 h-8 text-sm" value={recurring[t.id]?.start_date || ''}
                        onChange={e => setRecurring({ ...recurring, [t.id]: { ...recurring[t.id], start_date: e.target.value } })} />
                      <Input type="number" className="w-16 h-8 text-sm" placeholder="כמות" value={recurring[t.id]?.count || 4}
                        onChange={e => setRecurring({ ...recurring, [t.id]: { ...recurring[t.id], count: parseInt(e.target.value) || 4 } })} />
                      <select className="border rounded h-8 text-sm px-2" value={recurring[t.id]?.interval_days || 7}
                        onChange={e => setRecurring({ ...recurring, [t.id]: { ...recurring[t.id], interval_days: parseInt(e.target.value) } })}>
                        <option value={7}>שבועי</option>
                        <option value={14}>דו-שבועי</option>
                        <option value={30}>חודשי</option>
                        <option value={1}>יומי</option>
                      </select>
                      <Button size="sm" onClick={() => createRecurring(t.id)} className="bg-purple-600 hover:bg-purple-700 h-8">צור סדרה</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {templates.length === 0 && <p className="text-slate-400 text-center py-6">אין תבניות. צור אחת מעל ⬆️</p>}
            </div>
          </TabsContent>

          {/* DUPLICATE TOURNAMENT */}
          <TabsContent value="duplicate">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Copy className="w-5 h-5" />שכפול תחרות קיימת</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">בחר תחרות קיימת ושכפל אותה (ללא שחקנים, ללא תוצאות) — דרך מהירה ליצור תחרות זהה לתאריך אחר.</p>
                <Label>תחרות מקור</Label>
                <select className="w-full border rounded-lg p-2" value={duplicateTournament.id} onChange={e => setDuplicateTournament({ ...duplicateTournament, id: e.target.value })} data-testid="dup-tournament-select">
                  <option value="">-- בחר תחרות --</option>
                  {tournaments.map(t => <option key={t.id} value={t.id}>{t.name} ({(t.date || '').slice(0, 10)})</option>)}
                </select>
                <Label>תאריך לתחרות החדשה (אופציונלי)</Label>
                <Input type="datetime-local" value={duplicateTournament.date} onChange={e => setDuplicateTournament({ ...duplicateTournament, date: e.target.value })} />
                <Button onClick={doDuplicate} className="bg-indigo-600 hover:bg-indigo-700"><Copy className="w-4 h-4 ml-1" />שכפל תחרות</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* BULK + FORCE REGISTRATION */}
          <TabsContent value="bulk" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>הרשמה מאסיבית — הוסף כמה שחקנים בבת אחת</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Label>תחרות</Label>
                <select className="w-full border rounded-lg p-2" value={bulkTournament} onChange={e => setBulkTournament(e.target.value)} data-testid="bulk-tournament-select">
                  <option value="">-- בחר תחרות --</option>
                  {tournaments.filter(t => t.status !== 'completed').map(t => <option key={t.id} value={t.id}>{t.name} ({t.registered_players?.length || 0}/{t.max_players})</option>)}
                </select>
                <Label>שמות שחקנים (אחד בשורה או מופרדים בפסיק)</Label>
                <textarea data-testid="bulk-players-textarea" rows={6} className="w-full border rounded-lg p-3" value={bulkPlayers} onChange={e => setBulkPlayers(e.target.value)} placeholder="אלדד שמואלי&#10;דרור טל&#10;TOM GELLERT" />
                <p className="text-xs text-slate-500">💡 הקלד שם או חלק משם, או מייל. המערכת תזהה לבד.</p>
                <Button onClick={bulkRegister} className="bg-green-600 hover:bg-green-700"><Plus className="w-4 h-4 ml-1" />הוסף לתחרות</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2">⭐ Force Register — VIP / אורח</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">הוסף שחקן גם אם התחרות מלאה (עוקף max_players)</p>
                <select className="w-full border rounded-lg p-2" value={forceTournament} onChange={e => setForceTournament(e.target.value)}>
                  <option value="">-- בחר תחרות --</option>
                  {tournaments.filter(t => t.status !== 'completed').map(t => <option key={t.id} value={t.id}>{t.name} ({t.registered_players?.length || 0}/{t.max_players})</option>)}
                </select>
                <select className="w-full border rounded-lg p-2" value={forcePlayer} onChange={e => setForcePlayer(e.target.value)}>
                  <option value="">-- בחר שחקן --</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>)}
                </select>
                <Button onClick={forceRegister} className="bg-amber-600 hover:bg-amber-700">⭐ Force Register</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MARKETING */}
          <TabsContent value="marketing" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>תזכורת במייל + הודעה פנימית</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-600">שלח הודעה פנימית + מייל לכל המשתמשים <strong>שאינם רשומים</strong> לתחרות.</p>
                <select className="w-full border rounded-lg p-2" value={reminderTournament} onChange={e => setReminderTournament(e.target.value)}>
                  <option value="">-- בחר תחרות --</option>
                  {tournaments.filter(t => t.status === 'open').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <Button onClick={sendReminder} className="bg-pink-600 hover:bg-pink-700"><Send className="w-4 h-4 ml-1" />שלח תזכורות</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  WhatsApp Broadcast מתקדם
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">
                  שלח הודעה אישית בלחיצה אחת לכל קבוצת שחקנים. השתמש ב-<code className="bg-slate-100 px-1 rounded">{'{name}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{tournament}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{date}'}</code> להחלפה אוטומטית.
                </p>

                {/* Saved templates dropdown */}
                {msgTemplates.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <Label className="text-xs font-bold text-blue-900 mb-2 block">📚 תבניות שמורות ({msgTemplates.length})</Label>
                    <div className="flex flex-wrap gap-2">
                      {msgTemplates.map(tpl => (
                        <div key={tpl.id} className="inline-flex items-center bg-white border border-blue-300 rounded-full overflow-hidden">
                          <button onClick={() => useTemplate(tpl)} data-testid={`use-tpl-${tpl.id}`}
                            className="px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50">
                            ▶️ {tpl.name}
                          </button>
                          <button onClick={() => deleteMsgTemplate(tpl.id)} className="px-2 py-1 text-red-500 hover:bg-red-50 border-r border-blue-200">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recipient type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold">לאיזו קבוצה לשלוח?</Label>
                    <select className="w-full border rounded-lg p-2 mt-1" value={waRecipientType}
                      onChange={e => setWaRecipientType(e.target.value)}
                      data-testid="wa-recipient-type">
                      <option value="tournament_registered">שחקנים רשומים לתחרות</option>
                      <option value="tournament_waiting">רשימת המתנה של תחרות</option>
                      <option value="tournament_not_registered">שחקנים שלא נרשמו לתחרות</option>
                      <option value="by_skill_level">לפי דרג</option>
                      <option value="all_users">כל המשתמשים באתר</option>
                    </select>
                  </div>

                  {waRecipientType.startsWith('tournament_') && (
                    <div>
                      <Label className="text-xs font-bold">תחרות</Label>
                      <select className="w-full border rounded-lg p-2 mt-1" value={waTournament}
                        onChange={e => setWaTournament(e.target.value)}>
                        <option value="">-- בחר --</option>
                        {tournaments.map(t => <option key={t.id} value={t.id}>{t.name} ({(t.date || '').slice(0, 10)})</option>)}
                      </select>
                    </div>
                  )}

                  {waRecipientType === 'by_skill_level' && (
                    <div>
                      <Label className="text-xs font-bold">דרג</Label>
                      <select className="w-full border rounded-lg p-2 mt-1" value={waSkillLevel} onChange={e => setWaSkillLevel(e.target.value)}>
                        <option value="דרג א">דרג א</option>
                        <option value="דרג ב">דרג ב</option>
                        <option value="דרג ג">דרג ג</option>
                        <option value="יחידים open">יחידים open</option>
                        <option value="זוגות open">זוגות open</option>
                        <option value="נשים">נשים</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Message editor */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-bold">הודעה</Label>
                    <button onClick={saveCurrentAsTemplate} className="text-xs text-blue-600 hover:underline" data-testid="save-as-tpl-btn">
                      💾 שמור כתבנית
                    </button>
                  </div>
                  <textarea rows={5} className="w-full border rounded-lg p-3 text-sm font-mono" value={waMessage}
                    onChange={e => setWaMessage(e.target.value)}
                    data-testid="wa-message-textarea"
                    placeholder="היי {name}! ..." />
                </div>

                <Button onClick={generateWaAdvanced} className="bg-green-600 hover:bg-green-700 w-full" data-testid="wa-generate-btn">
                  <MessageCircle className="w-4 h-4 ml-1" />צור קישורי WhatsApp
                </Button>

                {/* Results */}
                {waLinks.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-green-50 border-b border-green-200 px-3 py-2 flex items-center justify-between">
                      <span className="text-sm font-bold text-green-900">{waLinks.length} קישורים מוכנים</span>
                      <button onClick={copyAllLinks} className="text-xs text-blue-600 hover:underline">📋 העתק הכל</button>
                    </div>
                    <div className="max-h-80 overflow-y-auto space-y-1 p-2 bg-slate-50">
                      {waLinks.map((w, i) => (
                        <a key={i} href={w.whatsapp_url} target="_blank" rel="noopener noreferrer"
                          data-testid={`wa-link-${i}`}
                          className="flex items-center justify-between gap-2 p-2 bg-white hover:bg-green-50 rounded border border-slate-200">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{w.name}</div>
                            <div className="text-xs text-slate-400" dir="ltr">{w.phone}</div>
                          </div>
                          <MessageCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick template creator */}
                <details className="border rounded-lg p-3 bg-slate-50">
                  <summary className="cursor-pointer text-sm font-bold text-slate-700">➕ צור תבנית חדשה</summary>
                  <div className="mt-3 space-y-2">
                    <Input placeholder="שם תבנית (למשל: תזכורת ערב התחרות)" value={newTplForm.name}
                      onChange={e => setNewTplForm({ ...newTplForm, name: e.target.value })} data-testid="new-tpl-name" />
                    <textarea rows={3} className="w-full border rounded-lg p-2 text-sm font-mono"
                      placeholder="תוכן ההודעה עם {name}, {tournament}, {date}..." value={newTplForm.content}
                      onChange={e => setNewTplForm({ ...newTplForm, content: e.target.value })} data-testid="new-tpl-content" />
                    <Button size="sm" onClick={saveMessageTemplate} className="bg-blue-600 hover:bg-blue-700" data-testid="new-tpl-save">
                      <Plus className="w-3 h-3 ml-1" />שמור תבנית
                    </Button>
                  </div>
                </details>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>קודי הנחה</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input placeholder="קוד (TENNIS50)" value={newCode.code} onChange={e => setNewCode({ ...newCode, code: e.target.value })} />
                  <Input type="number" placeholder="אחוז הנחה" value={newCode.discount_percent} onChange={e => setNewCode({ ...newCode, discount_percent: parseInt(e.target.value) || 10 })} />
                  <Input type="number" placeholder="מקסימום שימושים" value={newCode.max_uses} onChange={e => setNewCode({ ...newCode, max_uses: parseInt(e.target.value) || 100 })} />
                </div>
                <Button onClick={createDiscountCode} className="bg-amber-600 hover:bg-amber-700"><Tag className="w-4 h-4 ml-1" />צור קוד</Button>
                <div className="space-y-2 mt-3">
                  {discountCodes.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-3 border rounded-lg bg-slate-50">
                      <div>
                        <Badge className="bg-amber-100 text-amber-800 font-mono text-base">{c.code}</Badge>
                        <span className="text-sm text-slate-600 mr-3">{c.discount_percent}% הנחה · {c.used_count}/{c.max_uses} שימושים</span>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => deleteCode(c.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                  {discountCodes.length === 0 && <p className="text-slate-400 text-center py-3 text-sm">אין קודי הנחה</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LOYALTY */}
          <TabsContent value="loyalty">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-yellow-500" />נקודות נאמנות</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">כל 10 תחרויות שהסתיימו = 1 תחרות חינם. לחץ "מימוש" כדי לסמן שהמשתמש קיבל את הפרס.</p>
                {loyalty.length === 0 ? <p className="text-slate-400 text-center py-6">אין נתונים</p> : (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {loyalty.map((u, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${u.free_tournaments_available > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-slate-200'}`}>
                        <div>
                          <p className="font-medium text-slate-900">{u.full_name}</p>
                          <p className="text-xs text-slate-500">{u.tournaments_completed} תחרויות · {u.free_tournaments_earned} זכאויות · {u.free_tournaments_used} מומשו</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {u.free_tournaments_available > 0 && (
                            <>
                              <Badge className="bg-yellow-400 text-yellow-900 font-bold">{u.free_tournaments_available} זמינה</Badge>
                              <Button size="sm" onClick={() => redeemLoyalty(u.user_id)} className="bg-yellow-600 hover:bg-yellow-700">🎁 מימוש</Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Camera className="w-5 h-5" />גלריית תחרות</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Label>בחר תחרות</Label>
                <select className="w-full border rounded-lg p-2" value={photoTournament} onChange={e => setPhotoTournament(e.target.value)} data-testid="gallery-tournament-select">
                  <option value="">-- בחר תחרות --</option>
                  {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>

                {photoTournament && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t">
                      <Input placeholder="URL תמונה (https://...)" value={photoUrl} onChange={e => setPhotoUrl(e.target.value)} />
                      <Input placeholder="כיתוב (אופציונלי)" value={photoCaption} onChange={e => setPhotoCaption(e.target.value)} />
                      <Button onClick={addPhoto} className="bg-purple-600 hover:bg-purple-700"><Camera className="w-4 h-4 ml-1" />הוסף תמונה</Button>
                    </div>
                    <p className="text-xs text-slate-500">💡 העלה תמונה ל-Imgur/Cloudinary וקבל URL ישיר.</p>

                    {galleryPhotos.length > 0 && (
                      <div className="pt-3 border-t">
                        <h4 className="font-bold mb-2">תמונות בגלריה ({galleryPhotos.length})</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {galleryPhotos.map((p, idx) => (
                            <div key={p.id} className="relative border rounded-lg overflow-hidden bg-slate-100 group">
                              <img src={p.url} alt={p.caption || ''} className="w-full h-32 object-cover" />
                              {p.caption && <div className="p-1.5 text-xs bg-white">{p.caption}</div>}
                              <div className="absolute top-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                <button onClick={() => movePhoto(idx, -1)} disabled={idx === 0} className="bg-white/90 p-1 rounded shadow disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                                <button onClick={() => movePhoto(idx, 1)} disabled={idx === galleryPhotos.length - 1} className="bg-white/90 p-1 rounded shadow disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                                <button onClick={() => deletePhoto(p.id)} className="bg-red-500 text-white p-1 rounded shadow"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SYSTEM */}
          <TabsContent value="system" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Database className="w-5 h-5 text-emerald-600" />סנכרון סטטיסטיקות</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm text-slate-700 font-medium mb-1">חישוב מחדש של כל הסטטיסטיקות</p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    מחשב מחדש <strong>ניצחונות, הפסדים, מספר תחרויות ואחוז ניצחונות</strong> לכל המשתמשים על בסיס המשחקים והתחרויות שהסתיימו בפועל.
                    שימושי לסנכרון נתונים היסטוריים ישנים. <strong>הניקוד לא מושפע</strong> — הוא מתעדכן ידנית.
                  </p>
                </div>
                <Button
                  onClick={recomputeAllStats}
                  disabled={recomputing}
                  data-testid="recompute-stats-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 w-full md:w-auto"
                >
                  <RefreshCw className={`w-4 h-4 ml-1 ${recomputing ? 'animate-spin' : ''}`} />
                  {recomputing ? 'מחשב מחדש...' : 'חשב מחדש סטטיסטיקות לכל המשתמשים'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminAdvancedTools;
